import { SerializedConstructor } from '@langchain/core/load/serializable';

import { useCallback, useState } from 'react';

import { replaceCommitHash } from '@/Pages/EvaluatorPlayground/utils/resolveHubRefCommitHash';
import { deepEqualIgnoreUndefined } from '@/Pages/Playground/PlaygroundHome/hooks/usePlaygroundStore';
import { maybeReplaceModelProvider } from '@/Pages/Playground/utils/Playground.utils';
import useToast from '@/components/Toast/useToast';
import { FetchError } from '@/data/fetcher';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import {
  useCreateCommitMutation,
  useRepo,
  useRunRulesMutation,
} from '@/hooks/useSwr';
import {
  CommitManifestResponse,
  CreateRepoCommitResponse,
  DatasetRuleMutateBody,
  EvaluatorRuleSchema,
  RuleMutateBody,
  RuleSchema,
  SessionRuleMutateBody,
  StructuredEvaluatorSchema,
} from '@/types/schema';
import { hubApiCommitsPath } from '@/utils/constants';

import { useEvaluatorStore } from '../store/evaluatorStore';
import { getEvaluatorVersion } from './getEvaluatorVersion';
import { useLatestCommitFromHubRef } from './getLatestCommitFromHubRef';
import { useHubTypeManifest } from './useHubTypeManifest';

export const useCommitAndSaveRule = ({
  datasetId,
  sessionId,
  existingRule,
  transformManifest,
  onSuccessSavePrompt,
  onErrorSavePrompt,
  onSuccessSaveRule,
  onSuccess,
  onErrorSaveRule,
  hubRefManifest,
  getShouldMutateRule,
  filter,
  traceFilter,
  treeFilter,
}: {
  existingRule?:
    | DatasetRuleMutateBody
    | SessionRuleMutateBody
    | EvaluatorRuleSchema
    | undefined;
  transformManifest?: (
    manifest: SerializedConstructor
  ) => SerializedConstructor;
  onSuccessSavePrompt?: () => void;
  onErrorSavePrompt?: (error: Error) => void;
  onSuccessSaveRule?: (newRule: RuleSchema) => void;
  onSuccess?: (newRule: RuleSchema, commit?: CommitManifestResponse) => void;
  onErrorSaveRule?: (message?: string) => void;
  hubRefManifest: SerializedConstructor;
  datasetId?: string;
  sessionId?: string;
  getShouldMutateRule?: (commit: CommitManifestResponse | undefined) => boolean;
  filter?: string;
  traceFilter?: string;
  treeFilter?: string;
}) => {
  const [isSavingEvaluator, setIsSavingEvaluator] = useState(false);
  const hubRef = useEvaluatorStore((state) => state.hubRef);
  const { hubRefRepoName } = useLatestCommitFromHubRef(hubRef);
  const ruleId =
    existingRule && 'id' in existingRule ? existingRule.id : undefined;
  const mutate = useRunRulesMutation(ruleId);
  const repo = useRepo(undefined, hubRefRepoName);
  const fewShotEnabled = useEvaluatorStore((state) => state.fewShotEnabled);
  const name = useEvaluatorStore((state) => state.name);
  const backfillFrom = useEvaluatorStore((state) => state.backfillFrom);
  const samplingRate = useEvaluatorStore((state) => state.samplingRate);
  const numFewShotExamples = useEvaluatorStore(
    (state) => state.numFewShotExamples
  );
  const groupBy = useEvaluatorStore((state) => state.groupBy);
  const ffEvaluatorVersion = Number(
    useOrgConfig(OrgConfigs.new_rule_evaluator_creation_version).value
  );

  const { createToast } = useToast();

  const { manifest: hubManifestOriginal } = useHubTypeManifest();
  const doCommit = useCreateCommitMutation(undefined, hubRefRepoName, {
    onSuccess: () => {
      createToast({
        title: 'Created new commit for prompt.',
      });
      onSuccessSavePrompt?.();
    },
    onError: (error) => {
      if (!error.message.includes('Nothing to commit')) {
        createToast({
          title: 'Error committing to evaluator prompt',
          description: error.message,
        });
        onErrorSavePrompt?.(error);
      }
    },
  });

  const saveRule = useCallback(
    async ({
      validatedEvaluator: _validatedEvaluator,
    }: {
      validatedEvaluator:
        | StructuredEvaluatorSchema
        | ((commitHash: string | undefined) => StructuredEvaluatorSchema);
    }) => {
      let saveSuccess = false;
      try {
        setIsSavingEvaluator(true);
        if (!datasetId && !sessionId) {
          throw new Error(
            'Failed to create evaluator. Please try again or reach out to support.'
          );
        }
        if (datasetId && sessionId) {
          throw new Error(
            'Cannot provide both datasetId and sessionId. Please provide only one.'
          );
        }

        if (!name) {
          throw new Error('Rule name is required');
        }
        const validatedEvaluatorWithoutNewCommitHash =
          typeof _validatedEvaluator === 'function'
            ? _validatedEvaluator(undefined)
            : _validatedEvaluator;
        const evaluatorVersion = getEvaluatorVersion({
          oldEvaluator: existingRule?.evaluators?.[0].structured,
          newEvaluator: validatedEvaluatorWithoutNewCommitHash,
          oldRuleEvaluatorVersion:
            existingRule?.evaluator_version ?? ffEvaluatorVersion,
        });

        let commitRes: CreateRepoCommitResponse | undefined;

        if (
          !deepEqualIgnoreUndefined(hubRefManifest, hubManifestOriginal) &&
          hubRef !== '' &&
          evaluatorVersion >= 3
        ) {
          const transformedManifest = transformManifest
            ? transformManifest(hubRefManifest)
            : hubRefManifest;
          const manifestToCommit =
            maybeReplaceModelProvider(transformedManifest);

          try {
            commitRes = await doCommit.trigger({
              url: `${hubApiCommitsPath}/-/${hubRefRepoName}`,
              json: {
                parent_commit: repo.data?.repo?.last_commit_hash ?? null,
                manifest: manifestToCommit,
                skip_webhooks: [],
              },
            });
          } catch (e) {
            if (e instanceof FetchError && e.status === 409) {
              // This happens when the commit has no changes since the previous commit.
              // We try to track the "edited" state of the manifest so that we skip the
              // mutation in these cases and avoid this error, but if the user types some stuff
              // and then undoes it, they might reach here. We are fine to ignore these
            } else {
              throw e;
            }
          }
        }
        const validatedEvaluator =
          typeof _validatedEvaluator === 'function'
            ? _validatedEvaluator(commitRes?.commit?.commit_hash)
            : _validatedEvaluator;
        const shouldMutateRule = getShouldMutateRule
          ? getShouldMutateRule(
              commitRes?.commit
                ? {
                    commit_hash: commitRes?.commit?.commit_hash,
                    manifest: commitRes?.commit?.manifest,
                    examples: [],
                  }
                : undefined
            ) || !existingRule?.is_enabled
          : true;
        let newRule: RuleSchema | undefined;
        if (shouldMutateRule) {
          const model = validatedEvaluator.model
            ? maybeReplaceModelProvider(validatedEvaluator.model, true)
            : validatedEvaluator.model;
          newRule = await mutate.trigger({
            json: {
              ...(existingRule ?? {}),
              ...(datasetId
                ? { dataset_id: datasetId }
                : { session_id: sessionId }),
              is_enabled: true,
              sampling_rate: samplingRate,
              filter: filter ?? 'eq(is_root, true)',
              ...(traceFilter && { trace_filter: traceFilter }),
              ...(treeFilter && { tree_filter: treeFilter }),
              display_name: name,
              use_corrections_dataset: false,
              backfill_from: backfillFrom ?? undefined,
              // Upgrade evaluator version if the variable mapping has changed
              evaluator_version: evaluatorVersion,
              evaluators: [
                {
                  structured:
                    commitRes && 'hub_ref' in validatedEvaluator
                      ? {
                          ...validatedEvaluator,
                          model,
                          hub_ref: replaceCommitHash(
                            validatedEvaluator.hub_ref,
                            commitRes.commit.commit_hash
                          ),
                        }
                      : {
                          ...validatedEvaluator,
                          model,
                        },
                },
              ],
              ...(fewShotEnabled && {
                num_few_shot_examples: numFewShotExamples,
                use_corrections_dataset: true,
              }),
              ...(groupBy && { group_by: groupBy }),
            } as RuleMutateBody,
          });
          if (newRule) {
            onSuccessSaveRule?.(newRule);
          } else {
            createToast({
              title: 'Error',
              description: `Failed to save evaluator: ${mutate.error?.message}`,
              type: 'error',
            });
            onErrorSaveRule?.(mutate.error?.message);
          }
        }
        saveSuccess = true;
        if (newRule) {
          await onSuccess?.(
            newRule,
            commitRes?.commit
              ? {
                  commit_hash: commitRes?.commit?.commit_hash,
                  manifest: commitRes?.commit?.manifest,
                  examples: [],
                }
              : undefined
          );
        }
      } catch (e) {
        if (e instanceof Error) {
          createToast({
            title: 'Error',
            description: `Failed to save evaluator: ${e.message}`,
            type: 'error',
          });
          onErrorSaveRule?.(e.message);
        }
      } finally {
        setIsSavingEvaluator(false);
      }
      return saveSuccess;
    },
    [
      datasetId,
      sessionId,
      name,
      existingRule,
      ffEvaluatorVersion,
      hubRefManifest,
      hubManifestOriginal,
      hubRef,
      getShouldMutateRule,
      onSuccess,
      transformManifest,
      doCommit,
      hubRefRepoName,
      repo.data?.repo?.last_commit_hash,
      mutate,
      backfillFrom,
      samplingRate,
      fewShotEnabled,
      numFewShotExamples,
      onSuccessSaveRule,
      onErrorSaveRule,
      filter,
      traceFilter,
      treeFilter,
      createToast,
      groupBy,
    ]
  );
  return { saveRule, isSavingEvaluator };
};
